import { useEffect, useState, useCallback } from 'react';
import { useApi } from '@/api/axios';
import { pleasService } from '../services/pleasService';
import type { Plea } from '../types';

interface RefetchOptions {
  silent?: boolean;
}

export function usePleas(agreementUuid: string | undefined) {
  const api = useApi();
  const [pleas, setPleas] = useState<Plea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!agreementUuid) return;
    let cancelled = false;
    const fetchWithRetry = async (attempt = 1) => {
      setLoading(true);
      setError(null);
      try {
        const data = await pleasService.getPleas(api, agreementUuid);
        if (!cancelled) {
          setPleas(data);
          setLoading(false);
        }
      } catch {
        if (cancelled) return;
        if (attempt < 3) {
          setTimeout(() => fetchWithRetry(attempt + 1), 500 * Math.pow(2, attempt - 1));
        } else {
          setError('Erro ao buscar pedidos');
          setLoading(false);
        }
      }
    };
    fetchWithRetry();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreementUuid, refreshKey]);

  const refetch = useCallback(
    async (options: RefetchOptions = {}) => {
      if (!agreementUuid) return;
      if (options.silent) {
        try {
          const data = await pleasService.getPleas(api, agreementUuid);
          setPleas(data);
        } catch { /* swallow — user can retry */ }
      } else {
        setRefreshKey((k) => k + 1);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [agreementUuid],
  );

  return { pleas, loading, error, setError, refetch };
}
